import { TCommonFilters } from '@/features/wallets/types/index';
import { TMetadata } from '@/store/models/generics.model';

export const cardSortAvailableKeys = ['createdDate', 'id'] as const;
type TCardSortAvailableKeys = (typeof cardSortAvailableKeys)[number];

export type TCardSortAvailableValues = keyof Pick<IPaymentCard, Exclude<TCardSortAvailableKeys, 'id'>> | 'id';
export function isCardSortKeyAvailable(key: string): key is TCardSortAvailableValues {
  return key === 'id' || cardSortAvailableKeys.includes(key as TCardSortAvailableKeys);
}

export interface ISearchCard extends Record<string, string | number | boolean> {
  userId?: number;
  cardId?: number;
  publicToken?: string;
  pageNumber?: number;
  lockStatus?: number;
  pageCount?: number;
  isPhysical?: boolean;
  isConverted?: boolean;
  sortBy?: TCardSortAvailableValues;
  sortOrder?: 'ASC' | 'DESC';
  createdDateFrom?: string;
  createdDateTo?: string;
}

export interface IToggleCardOptions {
  foreign?: number;
  online?: number;
  atm?: number;
  nfc?: number;
  pinUnverified?: number;
}

export interface ICardSettings extends IToggleCardOptions {
  cardTag?: string;
}

export enum CardOptions {
  ATM = 'atm',
  FOREIGN = 'foreign',
  NFC = 'nfc',
  ONLINE = 'online',
}

export enum PaymentCardActionName {
  ACTIVATE = 'Activate',
  BLOCK = 'Block',
  LOCK = 'Lock',
  UNLOCK = 'Unlock',
  LOCKUNLOCK = 'LockUnlock',
  REGISTER_3DS_OTPSMS = 'Register3DS',
  UNBLOCKPIN = 'UnblockPIN',
  UNBLOCK_CVC = 'unblockcvc2',
  CONVERTVIRTUAL = 'ConvertVirtual',
  SETPIN = 'setPIN',
  LIMITS = 'Limits',
  REGISTER_3DS_OOB = 'authentication-methods',
}

export const cardContexts = {
  BULK: 'bulk',
  CARD: 'card',
  UNITARY: 'unitary',
};

export type TCardContext = keyof typeof cardContexts;

export const disabledCardStatus = {
  STOLEN: 'STOLEN',
  LOST: 'LOST',
  LOCK: 'LOCK',
  DESTROYED: 'DESTROYED',
  EXPIRED: 'EXPIRED',
  LOCK_INTERNAL: 'LOCK_INTERNAL',
  BLOCKED_PIN_LOCK: 'BLOCKED_PIN_LOCK',
  CUSTOM_LOCK: 'CUSTOM_LOCK',
} as const;

export const allcardStatus = {
  ...disabledCardStatus,
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  BLOCKED_PIN: 'BLOCKED_PIN',
  UNLOCK: 'UNLOCK',
} as const;

export type TCardStatus = keyof typeof allcardStatus;

export type TCardStatusColor = 'ACTIVE' | 'INACTIVE' | 'BLOCKED' | 'EXPIRED' | 'LOCK_INTERNAL' | 'CUSTOM' | 'DESTROYED';

export const renewalCardStatus = ['A', 'N'] as const;
export type TRenewalCardStatus = (typeof renewalCardStatus)[number];

export const CardStatusColor: Record<TCardStatus, TCardStatusColor> = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  LOCK: 'CUSTOM',
  UNLOCK: 'ACTIVE',
  STOLEN: 'BLOCKED',
  LOST: 'BLOCKED',
  BLOCKED_PIN: 'INACTIVE',
  BLOCKED_PIN_LOCK: 'INACTIVE',
  DESTROYED: 'DESTROYED',
  EXPIRED: 'EXPIRED',
  LOCK_INTERNAL: 'LOCK_INTERNAL',
  CUSTOM_LOCK: 'CUSTOM',
};

export enum PaymentBlockCardType {
  UNLOCK = 'UNLOCK',
  LOCK = 'LOCK',
  BLOCK_LOST = 'BLOCK_LOST',
  BLOCK_STOLEN = 'BLOCK_STOLEN',
  CLOSE = 'CLOSE',
  CUSTOM_LOCK = 'CUSTOM_LOCK',
  LOCK_INTERNAL = 'LOCK_INTERNAL',
  EXPIRED = 'EXPIRED',
}

export const CardBlockSettings = ['options', 'block', 'pin', 'limits', 'restrictions', 'digitized_cards'] as const;
export type TCardBlockType = (typeof CardBlockSettings)[number];

export type TFormatPaymentCardOptions = { options?: IToggleCardOptions; modifiedOption?: CardOptions };

export const PaymentBlockCardTypeCode: Record<PaymentBlockCardType, number> = {
  UNLOCK: 0,
  LOCK: 1,
  CUSTOM_LOCK: 7,
  BLOCK_LOST: 2,
  BLOCK_STOLEN: 3,
  LOCK_INTERNAL: 5,
  EXPIRED: 6,
  CLOSE: 4,
};

export const cardTypes = {
  VIRTUAL_CONVERTED: 'virtualConverted',
  VIRTUAL: 'virtual',
  PHYSICAL: 'physical',
  PHYSIC: 'physic',
};

export interface ICardPrint {
  label: string;
  value: number;
}

export const limitTypes = { atm: 'atm', payment: 'payment' } as const;
export const limitPeriod = { day: 'day', week: 'week', month: 'month', year: 'year', all: 'all' } as const;

export type TLimitTypes = typeof limitTypes;
export type TLimitPeriods = typeof limitPeriod;

export type TLimitType = keyof TLimitTypes;
export type TLimitPeriod = keyof TLimitPeriods;

export type TCardLimits<T = number> = Partial<Record<`limit${Capitalize<TLimitType>}${Capitalize<TLimitPeriod>}`, T>>;

export type TCardTotals<T = number> = Partial<Record<`total${Capitalize<TLimitType>}${Capitalize<TLimitPeriod>}`, T>>;
export type TCardTotalLimits<T = number> = Partial<
  Record<`totalLimit${Capitalize<TLimitType>}${Capitalize<TLimitPeriod>}`, T>
>;

export type TCardLimitsValues = Record<TLimitType, Record<TLimitPeriod, number>>;

export interface IPaymentCard extends TCardLimits, TCardTotals {
  cardId: number;
  userId: number;
  walletId: number;
  walletCardtransactionId?: number;
  mccRestrictionGroupId?: number;
  merchantRestrictionGroupId?: number;
  countryRestrictionGroupId?: number;
  publicToken?: string;
  cardTag?: string;
  statusCode?: TCardStatus;
  isLive?: number;
  pinTryExceeds?: number;
  maskedPan?: string;
  embossedName?: string;
  expiryDate?: string;
  CVV?: string;
  startDate?: string;
  endDate?: string;
  countryCode?: string;
  currencyCode?: string;
  lang?: string;
  deliveryTitle?: string;
  deliveryLastname?: string;
  deliveryFirstname?: string;
  deliveryAddress1?: string;
  deliveryAddress2?: string;
  deliveryAddress3?: string;
  deliveryCity?: string;
  deliveryPostcode?: string;
  deliveryCountry?: string;
  mobileSent?: string;
  limitsGroup?: string;
  permsGroup?: string;
  cardDesign?: string;
  virtualConverted?: number;
  optionAtm?: number;
  optionForeign?: number;
  optionOnline?: number;
  optionNfc?: number;
  paymentDailyLimit?: number;
  physical?: number;
  restrictionGroupLimits?: IPaymentCardRestrictionGroupLimit[];
  createdBy?: number;
  createdDate?: string;
  modifiedBy?: number;
  modifiedDate?: string;
  totalRows?: number;
  renewalType?: TRenewalCardStatus;
  renewalDate?: string;
  originalCardId?: number;
  sendToParent?: number;
  designCode?: string;
  cardLanguages?: string;
  eventName?: string;
  eventAlias?: string;
  cancellationNumber?: string;
  metadata?: TMetadata;
  logoId?: string;
  logoBackId?: string;
  packageId?: string;
  customizeInfo?: string;
  letterCustomizedInfo?: string;
  freeCustomizedInfo?: string;
  deliveryMethod?: string;
  pinMailer?: number;
  batchDeliveryId?: number;
}

export interface ICardUnitCreationRequest {
  cardPrint: number;
  userId: number;
  walletId: number;
  permsGroup: string;
  cardTag?: string;
  sendToParent: number;
  limitAtmMonth: number;
  limitAtmWeek: number;
  limitAtmDay: number;
  limitPaymentMonth: number;
  limitPaymentWeek: number;
  limitPaymentDay: number;
}

export interface IPaymentCardRestrictionGroupLimit {
  paymentDailyLimit?: number;
  mccRestrictionGroups?: number;
  countryRestrictionGroups?: number;
  merchantIdRestrictionGroups?: number;
}

export const ATM_CODES = [6010, 6011];

export const cardTransactionPaymentStatus = {
  A: 'Authorization',
  R: 'Refund',
  S: 'Settlement',
  M: 'MoneySend',
  C: 'Clearing',
  I: 'Decline',
  V: 'Reversal',
} as const;

export type TCardTransactionPaymentStatus = keyof typeof cardTransactionPaymentStatus;

export const authorizationMti = {
  420: 'Reversal',
  0: 'Reversal',
  1240: 'Clearing',
  100: 'Authorization',
  120: 'Advice',
  400: 'Reversal',
} as const;

export type TAuthorizationMti = keyof typeof authorizationMti;

const authorizationStatuses = {
  APPROVE: 'APPROVE',
  CALL_ISSUER: 'CALL_ISSUER',
  CAPTURE: 'CAPTURE',
  DECLINE: 'DECLINE',
} as const;

export type TAuthorizationStatus = (typeof authorizationStatuses)[keyof typeof authorizationStatuses];

export const authorizationCodeMap = {
  [authorizationStatuses.APPROVE]: ['0', '00', '8', '08', '10', '85', '87'],
  [authorizationStatuses.CALL_ISSUER]: ['01'],
  [authorizationStatuses.CAPTURE]: ['41', '43'],
  [authorizationStatuses.DECLINE]: [
    '6',
    '06',
    '03',
    '04',
    '05',
    '12',
    '13',
    '14',
    '15',
    '17',
    '30',
    '32',
    '34',
    '46',
    '51',
    '54',
    '55',
    '57',
    '58',
    '59',
    '61',
    '62',
    '63',
    '65',
    '68',
    '70',
    '71',
    '72',
    '75',
    '76',
    '77',
    '78',
    '79',
    '81',
    '82',
    '86',
    '88',
    '89',
    '91',
    '92',
    '94',
    '96',
  ],
} as const;

export type TAuthorizationResponseCode = (typeof authorizationCodeMap)[TAuthorizationStatus][number];

const codeToStatusMap = Object.entries(authorizationCodeMap).reduce(
  (acc, [status, codes]) => {
    codes.forEach((code) => (acc[code] = status as TAuthorizationStatus));
    return acc;
  },
  {} as Record<string, TAuthorizationStatus>,
);

export function getAuthorizationStatus(code: string): TAuthorizationStatus | undefined {
  return codeToStatusMap[code];
}

export const cardSubTypes = ['OFT', 'PRE', 'SEP'] as const;
export type TCardTransactionSubType = (typeof cardSubTypes)[number];

export const cardHolderPresence = {
  PRESENT: 'PRESENT',
  NOT_PRESENT: 'NOT_PRESENT',
  UNKNOWN: 'UNKNOWN',
} as const;

export type TCardHolderPresenceStatus = keyof typeof cardHolderPresence;

export const cardHolderPresenceMap = {
  '0': cardHolderPresence.PRESENT,
  '1': cardHolderPresence.NOT_PRESENT,
  '2': cardHolderPresence.NOT_PRESENT,
  '3': cardHolderPresence.NOT_PRESENT,
  '4': cardHolderPresence.NOT_PRESENT,
  '5': cardHolderPresence.NOT_PRESENT,
  '6': cardHolderPresence.NOT_PRESENT,
  '9': cardHolderPresence.UNKNOWN,
} as const;

export type TCardHolderPresenceType = keyof typeof cardHolderPresenceMap;

export const entryPANMethods = ['0', '1', '2', '3', '4', '5', '7', '10', '79', '80', '81', '91'] as const;

export type TEntryPANMethodType = (typeof entryPANMethods)[number];

export const exemptionType3DS = [
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  'A',
  'C',
  'D',
  'M',
  'O',
  'R',
  'T',
  'V',
] as const;

export type T3DSExemptionType = (typeof exemptionType3DS)[number];

export interface ICardTransaction extends TCardTotalLimits {
  cardtransactionId?: number;
  userId?: number;
  cardId?: number;
  walletId?: number;
  acquirerId?: number;
  walletCurrency?: string;
  merchantId?: string;
  optimizedMerchantName?: string;
  merchantCategory?: string;
  merchantCategoryGroup?: string;
  merchantCategoryType?: string;
  merchantName?: string;
  merchantAddress?: string;
  merchantCity?: string;
  merchantCountry?: string;
  merchantLogo?: string;
  mccCode?: string;
  paymentLocalTime?: string;
  publicToken?: string;
  paymentAmount?: string;
  paymentCurrency?: string;
  transactionSubType?: TCardTransactionSubType;
  fees?: string;
  paymentCountry?: string;
  paymentId?: string;
  paymentStatus?: TCardTransactionPaymentStatus;
  paymentLocalAmount?: string;
  paymentLocalDate?: string;
  is3DS?: boolean;
  '3dsExemptionType'?: T3DSExemptionType;
  posCardholderPresence?: TCardHolderPresenceType;
  posPostcode?: string;
  posCountry?: string;
  posTerminalId?: string;
  posCardPresence?: string;
  panEntryMethod?: TEntryPANMethodType;
  authorizationNote?: string;
  authorizationResponseCode?: TAuthorizationResponseCode;
  authorizationIssuerId?: string;
  authorizationIssuerTime?: string;
  authorizationMti?: TAuthorizationMti;
  authorizedBalance?: string;
  totalRows?: number;
  cardDigitalizationExternalId?: string;
}

export const digitizedCardStatus = ['A', 'D', 'S', 'U', 'X'] as const;
export type TDigitizedCardStatus = (typeof digitizedCardStatus)[number];

export const digitizedCardSuspendStatus = ['suspend', 'unsuspend'] as const;
export type TDigitizedCardSuspendStatus = (typeof digitizedCardSuspendStatus)[number];

export const digitizedCardReasonCode = ['L', 'S', 'T', 'F', 'Z'] as const;
export type TDigitizedCardReasonCode = (typeof digitizedCardReasonCode)[number];

export function isCardReasonCode(code: string): code is TDigitizedCardReasonCode {
  return ([...digitizedCardReasonCode] as string[]).includes(code);
}

export const digitizedCardActions = {
  DELETE: 'delete',
  SUSPEND: digitizedCardSuspendStatus[0],
  UNSUSPEND: digitizedCardSuspendStatus[1],
} as const;
type TDigitizedCardActionsKeys = keyof typeof digitizedCardActions;
export type TDigitizedCardActions = (typeof digitizedCardActions)[TDigitizedCardActionsKeys];

export const digitizedCardActionsCodes: {
  [key in TDigitizedCardActions]: TDigitizedCardReasonCode[];
} = {
  suspend: digitizedCardReasonCode.filter((value) => value != 'F'),
  unsuspend: digitizedCardReasonCode.filter((value) => value != 'L' && value != 'S'),
  delete: digitizedCardReasonCode.filter((value) => value != 'F'),
};

export interface IDigitizedCardAction {
  id: string;
  cardId: string;
  status: TDigitizedCardStatus;
  createdDate?: string;
  modifiedDate?: string;
}

export type TTokenRequestor =
  | 'ADYEN'
  | 'AMAZON'
  | 'GOOGLE_PAY'
  | 'APPLE_PAY'
  | 'ASIA_PAY'
  | 'CHUNGHWA'
  | 'FITBIT_PAY'
  | 'GARMIN_PAY'
  | 'LG_ELECTRONICS'
  | 'MASTERPASS'
  | 'MICROSOFT'
  | 'MERCHANT_TOKEN_PROGRAM'
  | 'MONTBLANC_PAY'
  | 'PHYRE'
  | 'RELIANCE'
  | 'SAUDI_ARABIA_MONETARY_AUTHORITY'
  | 'SAMSUNG_PAY'
  | 'SECURE_CO'
  | 'STOCARD_PAY'
  | 'VISA_DIGITAL_COMMERCE_APP'
  | 'VISA_CHECKOUT'
  | 'WORLD_PAY';

export type TDeviceType =
  | 'A'
  | 'B'
  | 'C'
  | 'D'
  | 'E'
  | 'F'
  | 'G'
  | 'H'
  | 'J'
  | 'M'
  | 'N'
  | 'P'
  | 'R'
  | 'S'
  | 'T'
  | 'U'
  | 'V'
  | 'W'
  | 'X';

export interface IDigitizedCard {
  id?: string;
  cardId?: number;
  status?: TDigitizedCardStatus;
  walletProvider?: string;
  tokenRequestor?: TTokenRequestor;
  tokenUniqueReference?: string;
  merchantName?: string;
  deviceType?: TDeviceType;
  deviceId?: string;
  deviceName?: string;
  activationMethod?: string;
  tokenServiceProvider?: string;
  expirationDate?: string;
  createdDate?: string;
  modifiedDate?: string;
  cardDigitalizationExternalId?: string;
}

export const CardTransactionPaymentTypes = {
  Withdrawal: 'Withdrawal',
  Payment: 'Payment',
  digitizedCardPayment: 'digitizedCardPayment',
  with3DS: 'with3DS',
  without3DS: 'without3DS',
} as const;

export type TPaymentType = keyof typeof CardTransactionPaymentTypes;

export const paymentTypes: TPaymentType[] = ['Withdrawal', 'Payment', 'digitizedCardPayment'] as const;
export const cardPaymentTypes: TPaymentType[] = ['with3DS', 'without3DS'] as const;

export const checkedValues = { TRUE: true, FALSE: false, INDETERMINATE: 'indeterminate' } as const;
export type TCheckedValues = (typeof checkedValues)[keyof typeof checkedValues];

export type TCardTransactionsFilters = TCommonFilters & { merchantId?: string; status?: string };

export type TCardTransactionFormFilters = {
  paymentId?: string;
  walletId?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  amountFrom?: number;
  amountTo?: number;
  paymentStatus?: TCardTransactionPaymentStatus[];
  mcc?: string;
  merchantId?: string;
  paymentTypes?: TPaymentType[];
  is3DS?: number;
  xpay?: number;
};

export interface ITransactionByDigitizedCard extends ICardTransaction {
  tokenRequestor?: TTokenRequestor;
  deviceType?: TDeviceType;
  deviceName?: string;
  isLinkedToDizitedcard?: boolean;
}

export interface IEnrollment {
  publicToken: string;
  id: string;
  method: 'OTPSMS' | 'OOB';
  system: string;
}

export const simulationCardStatus = ['A', 'S'] as const;
export type TSimulationCardStatus = (typeof simulationCardStatus)[number];

export interface ITransactionSimulation {
  date: string;
  amount: number;
  mcc: string;
  merchantId: string;
  merchantName: string;
  paymentStatus: TSimulationCardStatus;
  paymentCode: string;
}
